import { ReactNode, useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import {
  Notification,
  setNotificationListener,
} from "../utils/notify";

/**
 * Renders notifications raised anywhere in the app, replacing the
 * blocking notify() calls this codebase used to rely on.
 *
 * Messages queue rather than overwrite: a failing screen can raise
 * several, and dropping all but the last would hide the first cause.
 */
const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<Notification[]>([]);
  const current = queue[0];

  useEffect(() => {
    setNotificationListener((notification) =>
      setQueue((prev) => [...prev, notification]),
    );
    return () => setNotificationListener(null);
  }, []);

  const handleClose = (_?: unknown, reason?: string) => {
    // Errors stay until dismissed; a message the user missed is a
    // message they cannot act on.
    if (reason === "clickaway") return;
    setQueue((prev) => prev.slice(1));
  };

  return (
    <>
      {children}
      <Snackbar
        // `key` restarts the timer when the next message takes over.
        key={current?.message}
        open={!!current}
        autoHideDuration={current?.severity === "error" ? null : 5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => handleClose()}
          severity={current?.severity ?? "info"}
          variant="filled"
          sx={{ maxWidth: 420, whiteSpace: "pre-line" }}
        >
          {current?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationProvider;
